import {
  Component,
  DestroyRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Store, select } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

import {
  FeedService,
  DimensionAttachParams,
} from 'src/app/core/services/feed.service';
import { ObjectCreatorService } from 'src/app/core/services/object-creator.service';

import { ToolbarButton } from '../../../models/toolbar.models';

import {
  Filter,
  FilterItem,
  PeriodFilterItem,
} from 'src/app/core/models/core.models';
import {
  NomObjectClass,
  DimensionAttachTypeId,
  NomDateTimeFormats,
} from 'src/app/core/constants';

import {
  loadFilter,
  clearFilters,
  toggleFilter,
  activateFilters,
  loadFeedFilters,
  selectFilterItem,
  deselectFilterItem,
  deactivateFilterItem,
  updateActiveFilterItem,
} from 'src/app/core/actions/feed.actions';

import {
  selectFeed,
  getContentFilters,
  selectFeedFilters,
  selectFeedActiveFilterItems,
  selectFeedSelectedFilterItems,
} from 'src/app/core/reducers';
import { AppState } from 'src/app/reducers';

import {
  map,
  tap,
  take,
  first,
  combineLatest,
  withLatestFrom,
  BehaviorSubject,
  combineLatestWith,
  Subject,
  switchMap,
  EMPTY,
  Observable,
} from 'rxjs';
import { AnyFilterItem } from 'src/app/core/reducers/feed.reducer';
import { formatDate } from 'date-fns';
import { Actions, ofType } from '@ngrx/effects';
import { objectChanged } from 'src/app/core/actions/core.actions';

export enum FilterTab {
  Filters = 'filters',
  ActiveFilters = 'active-filters',
}

interface ActiveFilterGroup {
  key: string; // e.g. property_uri
  title: string; // filter_title / property title
  items: AnyFilterItem[];
}

@Component({
  selector: 'app-filters',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FiltersComponent {
  readonly FilterTab = FilterTab;

  currentTab: FilterTab = FilterTab.Filters;
  hasActiveFilters = false;
  searchFilter = '';
  activeFiltersCount = 0;
  countSelectedItems = 0;
  faSpinner = faSpinner;

  sliderValue: number;

  private filtersMap = new Map<string, Filter>();
  private searchFilterSubject = new Subject<string>();
  private currentTabSubject = new BehaviorSubject<FilterTab>(FilterTab.Filters);
  private actions$ = inject(Actions);

  filters$ = this.store.select(selectFeedFilters);
  selectedFilterItems$ = this.store.select(selectFeedSelectedFilterItems);
  activeFilterItems$ = this.store.select(selectFeedActiveFilterItems);

  activeFilterGroups$: Observable<ActiveFilterGroup[]> = this.store
    .select(selectFeedActiveFilterItems)
    .pipe(
      map((items: AnyFilterItem[]) => {
        const groups = new Map<string, ActiveFilterGroup>();

        for (const item of items) {
          const key = item.property_uri; // grouping key
          // Prefer filter_title, fallback to property_uri
          const title = item.filter_title || item.title || item.property_uri;

          let group = groups.get(key);
          if (!group) {
            group = { key, title, items: [] };
            groups.set(key, group);
          }

          group.items.push(item);
        }

        return Array.from(groups.values());
      }),
      // The group list changes as filters are toggled; re-derive which panels are open.
      tap((groups) => this.reconcileAccordionOpenState(groups)),
    );

  /**
   * Which active-filter panels are expanded, for the `<sui-accordion [multiple]>`.
   *
   * The accordion is *controlled* — it has no internal memory of open/closed, so we own that
   * state here. Because the group list is rebuilt whenever filters change, we reconcile on every
   * emission to keep the two behaviours the old ngbAccordion gave for free:
   *   1. a newly-added group starts **expanded**, and
   *   2. a group the user **collapsed stays collapsed** (it isn't forced back open).
   */
  activeFilterOpenKeys: string[] = [];

  /** Group keys seen last time — lets us distinguish "user collapsed it" from "brand new group". */
  private knownGroupKeys = new Set<string>();

  private reconcileAccordionOpenState(groups: ActiveFilterGroup[]): void {
    const presentKeys = new Set(groups.map((g) => g.key));

    // Keep panels that are still open and still exist (drops groups that went away).
    const stillOpen = this.activeFilterOpenKeys.filter((key) =>
      presentKeys.has(key),
    );
    // Expand groups we've never seen before (the "new groups start open" default).
    const newlyAdded = groups
      .map((group) => group.key)
      .filter((key) => !this.knownGroupKeys.has(key));

    this.activeFilterOpenKeys = [...stillOpen, ...newlyAdded];
    this.knownGroupKeys = presentKeys;
  }

  private getButtonsConfig(
    hasItems: boolean,
    isActiveTab: boolean,
  ): ToolbarButton[] {
    const buttons: ToolbarButton[] = [];

    // ! Buttons shown in both Tabs
    buttons.push({
      type: 'button',
      variant: 'secondary',
      label: this.translateService.instant('filters.button.clear'),
      action: this.clearFilters.bind(this),
      disabled: isActiveTab ? false : !hasItems, // ! disabled in "All Filters" Tab when no items
    });

    // ! Activate button only shown in "All Filters" Tab
    if (!isActiveTab) {
      buttons.push({
        type: 'button',
        variant: 'primary',
        label: this.translateService.instant('filters.button.activate'),
        action: this.activateFilters.bind(this),
        disabled: !hasItems,
      });
    }

    return buttons;
  }

  buttons$ = combineLatest([
    this.selectedFilterItems$,
    this.activeFilterItems$,
    this.currentTabSubject,
  ]).pipe(
    map(([selectedItems, activeItems, currentTab]) => {
      const isActiveTab = currentTab === FilterTab.ActiveFilters;
      const items = isActiveTab ? activeItems : selectedItems;
      const hasItems = items.length > 0;

      return this.getButtonsConfig(hasItems, isActiveTab);
    }),
  );

  // ! Define filteredFilters$ based on filters$ and search
  filteredFilters$ = combineLatest([
    this.filters$,
    this.searchFilterSubject.asObservable(),
  ]).pipe(
    map(([filters, searchText]) => {
      this.searchFilter = searchText;

      if (!searchText.trim()) {
        // If empty or only spaces, return all filters
        return filters;
      }

      const lowerSearch = searchText.toLowerCase();
      return filters.filter((item) =>
        item.name.toLowerCase().includes(lowerSearch),
      );
    }),
  );

  protected destroyRef = inject(DestroyRef);

  constructor(
    private store: Store<AppState>,
    private translateService: TranslateService,
    private feedService: FeedService,
    private objectCreatorService: ObjectCreatorService,
  ) {
    this.store
      .select(selectFeedActiveFilterItems)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((items) => {
          this.hasActiveFilters = items.length > 0;
          this.activeFiltersCount = items.length;
          if (
            this.currentTab === FilterTab.ActiveFilters &&
            !this.hasActiveFilters
          ) {
            this.currentTab = FilterTab.Filters;
            this.currentTabSubject.next(FilterTab.Filters);
          }
        }),
      )
      .subscribe();

    this.selectedFilterItems$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((items) => {
          this.countSelectedItems = items?.length || 0;
        }),
      )
      .subscribe();

    this.store
      .pipe(
        select(selectFeed),
        withLatestFrom(this.store.select(selectFeedFilters)),
        first(),
        takeUntilDestroyed(this.destroyRef),
        tap(([feed, filters]) => {
          if (!filters || filters.length === 0) {
            this.store.dispatch(loadFeedFilters({ feedId: feed.id }));
          }
        }),
      )
      .subscribe();
  }

  ngOnInit() {
    this.actions$
      .pipe(
        ofType(objectChanged),
        withLatestFrom(this.store.select(selectFeed)),
        tap(([, feed]) => {
          if (!feed) return;

          this.store.dispatch(loadFeedFilters({ feedId: feed.id }));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  activateFilters() {
    this.store.dispatch(activateFilters());
    // ! switch to "Active Filters" tab after activation
    this.currentTab = FilterTab.ActiveFilters;
    this.currentTabSubject.next(FilterTab.ActiveFilters);
  }

  clearFilters() {
    this.store.dispatch(clearFilters());
    this.countSelectedItems = 0;
    this.sliderValue = 1;
    // ! force Tab switch when clearing filters
    if (this.currentTab === FilterTab.ActiveFilters) {
      this.currentTab = FilterTab.Filters;
      this.currentTabSubject.next(FilterTab.Filters);
    }
  }

  showMore(filter: Filter) {
    this.store
      .select(selectFeed)
      .pipe(
        combineLatestWith(this.store.select(getContentFilters)),
        // ! important to take only the first, as the action modifies state
        first(),
        tap(([feed, contentFilters]) => {
          //console.log('show more', filter);
          this.store.dispatch(
            loadFilter({
              feedId: feed.id,
              filter: filter,
              contentFilters: contentFilters,
              offset: filter.offset + (filter.limit || 30),
            }),
          );
        }),
      )
      .subscribe();
  }

  onSearchFilterChange(searchFilter: string) {
    this.searchFilterSubject.next(searchFilter);
  }

  handleAllFiltersSliderChange(
    value: number,
    filter: Filter,
    filterItem: AnyFilterItem,
  ) {
    this.sliderValue = value;

    switch (this.sliderValue) {
      case 0:
        this.store.dispatch(
          selectFilterItem({
            propertyUri: filter.property_uri_facet,
            filterItem,
            mode: 'exclude',
          }),
        );
        break;
      case 1:
        // * check if item is currently active
        if (filterItem.active) {
          // * if active -> deactivate from both arrays
          this.store.dispatch(
            deactivateFilterItem({
              propertyUri: filter.property_uri_facet,
              filterItem,
            }),
          );
        } else {
          // * if not active -> just deselect from selectedFilterItems
          this.store.dispatch(
            deselectFilterItem({
              propertyUri: filter.property_uri_facet,
              filterItem,
              mode: filterItem.type || 'include',
            }),
          );
        }
        break;
      case 2:
        this.store.dispatch(
          selectFilterItem({
            propertyUri: filter.property_uri_facet,
            filterItem,
            mode: 'include',
          }),
        );
        break;
      default:
        break;
    }
  }

  handleActiveFiltersSliderChange(value: number, filterItem: AnyFilterItem) {
    this.sliderValue = value;

    switch (this.sliderValue) {
      case 0:
        // * update both selectedFilterItems and activeFilterItems immediately
        this.store.dispatch(
          updateActiveFilterItem({
            propertyUri: filterItem.property_uri,
            filterItem,
            mode: 'exclude',
          }),
        );
        break;
      case 1:
        // * always deactivate (remove from both arrays) in Active Filters tab
        this.store.dispatch(
          deactivateFilterItem({
            propertyUri: filterItem.property_uri,
            filterItem,
          }),
        );
        break;
      case 2:
        // * update both selectedFilterItems and activeFilterItems immediately
        this.store.dispatch(
          updateActiveFilterItem({
            propertyUri: filterItem.property_uri,
            filterItem,
            mode: 'include',
          }),
        );
        break;
      default:
        break;
    }
  }

  getItemCheckType(filterItem: AnyFilterItem): number {
    if (!filterItem.checked) {
      return 1;
    }
    return filterItem.type === 'include' ? 2 : 0;
  }

  toggleFilter(filter: Filter) {
    this.store.dispatch(toggleFilter({ filter }));
  }

  // ! Tabs related ccde - Start
  switchTab(tab: FilterTab): void {
    if (tab === FilterTab.ActiveFilters && !this.hasActiveFilters) {
      return;
    }
    this.currentTab = tab;
    this.currentTabSubject.next(tab);
  }

  isTabActive(tab: FilterTab): boolean {
    return this.currentTab === tab;
  }

  isTabDisabled(tab: FilterTab): boolean {
    if (tab === FilterTab.ActiveFilters) {
      return !this.hasActiveFilters;
    }
    return false;
  }

  hasCheckedItems(filter: Filter): boolean {
    return filter.items.some((item) => item.checked);
  }

  getCheckedItemsCount(filter: Filter): number {
    return filter.items.filter((item) => item.checked).length;
  }

  shouldShowFilter(filter: Filter): boolean {
    return this.currentTab === FilterTab.ActiveFilters
      ? filter.items.some((item) => item.active)
      : filter.items.some((item) => item.checked);
  }

  shouldShowFilterItem(item: AnyFilterItem): boolean {
    return this.currentTab === FilterTab.ActiveFilters
      ? item.active
      : item.checked;
  }

  getFilterItemCount(filter: Filter): number {
    return this.currentTab === FilterTab.ActiveFilters
      ? filter.items.filter((item) => item.active).length
      : filter.items.filter((item) => item.checked).length;
  }
  // ! Tabs related ccde - End

  saveAsDimension() {
    this.store
      .select(getContentFilters)
      .pipe(
        take(1),
        switchMap((contentFilters) =>
          this.feedService.getSolrQueryFromContentFilters(contentFilters),
        ),
        take(1),
        tap((query) => {
          this.objectCreatorService.createNewDimension(query);
        }),
      )
      .subscribe();
  }

  attachAndSaveAsDimension() {
    combineLatest([
      this.store.select(selectFeed),
      this.store.select(getContentFilters),
    ])
      .pipe(
        take(1),
        switchMap(([feed, contentFilters]) => {
          if (!feed) {
            console.error('No feed available for dimension attachment');
            return EMPTY;
          }

          // * convert filters to Query
          return this.feedService
            .getSolrQueryFromContentFilters(contentFilters)
            .pipe(
              tap((query) => {
                // * create Dimension with attachment params
                const dimensionAttachParams: DimensionAttachParams = {
                  object_id: feed.id,
                  object_class_id: NomObjectClass.FEED,
                  attach_type_id: DimensionAttachTypeId.FILTER,
                };

                // Open popup with attachment params
                this.objectCreatorService.createNewDimension(
                  query,
                  dimensionAttachParams,
                );
              }),
            );
        }),
      )
      .subscribe();
  }

  getPeriodItemTitle(item: AnyFilterItem): string {
    if (item.kind !== 'period') {
      return '';
    }

    const label =
      formatDate(item.time_start * 1000, NomDateTimeFormats.ANALYTICS_LONG) +
      ' - ' +
      formatDate(item.time_end * 1000, NomDateTimeFormats.ANALYTICS_LONG);

    return label;
  }
}
